import React from 'react';
import { useSelector } from 'react-redux';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Card, 
  CardContent,
  Alert,
  Chip,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Skeleton,
  Stack,
  IconButton,
  Tooltip,
  Container,
  TextField,
  InputAdornment,
  Button,
  Fade,
  Collapse,
  alpha // IMPORTED: For theme-aware color manipulation
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { 
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  People as PeopleIcon,
  AccountBalance as AccountBalanceIcon,
  EmojiEvents as EmojiEventsIcon,
  Analytics as AnalyticsIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';
import { useGetCustomersQuery, useLazySearchCustomerByPhoneQuery } from '../../store/api/dashboardApi';
import { formatCurrency } from '../../utils/formatters';

const Customers = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
  // Search state
  const [searchInput, setSearchInput] = React.useState('');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [searchMode, setSearchMode] = React.useState(false);
  
  // Get filters from Redux (including date range)
  const dashboardFilters = useSelector((state) => state.dashboard.filters);
  
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: isMobile ? 10 : 25,
  });

  // UPDATED: Main query is now skipped during a search to prevent redundant API calls.
  // Uses dashboard filters including date range
  const { 
    data: customersData, 
    isLoading, 
    error,
    refetch,
    isFetching 
  } = useGetCustomersQuery(dashboardFilters, {
    skip: searchMode,
  });

  // UPDATED: Lazy query is the sole search mechanism. `data` is renamed for direct use.
  const [
    searchByPhone, 
    { 
      data: searchResults, 
      isLoading: isSearching, 
      error: searchError 
    }
  ] = useLazySearchCustomerByPhoneQuery();

  React.useEffect(() => {
    setPaginationModel(prev => ({
      ...prev,
      pageSize: isMobile ? 10 : 25
    }));
  }, [isMobile]);

  // Handle search input change with debounce
  const searchTimeoutRef = React.useRef();

  const handleSearchInputChange = (event) => {
    const value = event.target.value;
    setSearchInput(value);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      const trimmedQuery = value.trim();
      if (trimmedQuery) {
        setSearchQuery(trimmedQuery);
        setSearchMode(true);
        searchByPhone(trimmedQuery);
      } else {
        clearSearch();
      }
    }, 500); // 500ms debounce
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    setSearchMode(false);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  };

  // REMOVED: Redundant useEffect for setting search results state.

  // UPDATED: Simplified data retrieval logic.
  const getDisplayData = () => {
    if (searchMode) {
      if (searchResults?.found) {
        const customer = searchResults.customer;
        return {
          summary: {
            totalCustomers: 1,
            totalPlayAmount: customer.totalPlayAmount,
            totalWinnings: customer.totalWinnings,
            avgCustomerValue: customer.totalPlayAmount,
            groupedBy: 'Phone Search'
          },
          customers: [customer]
        };
      }
      return {
        summary: { totalCustomers: 0, totalPlayAmount: 0, totalWinnings: 0, avgCustomerValue: 0, groupedBy: 'Phone Search' },
        customers: []
      };
    }
    return customersData;
  };

  const displayData = getDisplayData();
  const summary = displayData?.summary || {};
  const customers = displayData?.customers || [];

  const rows = React.useMemo(() => {
    return customers.map((customer, index) => ({
      id: customer.userId || customer.phone || `customer-${index}`,
      ...customer,
      firstBetDate: customer.firstBetDate ? new Date(customer.firstBetDate) : null,
      lastBetDate: customer.lastBetDate ? new Date(customer.lastBetDate) : null,
    }));
  }, [customers]);

  const getColumns = React.useCallback(() => {
    const baseColumns = [
        { 
            field: 'phone', 
            headerName: 'Phone', 
            width: 140,
            minWidth: 120,
            flex: 1,
            renderCell: (params) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PhoneIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                <Typography 
                variant="body2" 
                sx={{ 
                    fontFamily: 'monospace',
                    fontSize: { xs: '0.75rem', md: '0.875rem' },
                    fontWeight: searchMode ? 'bold' : 'normal',
                    color: searchMode ? 'primary.main' : 'inherit'
                }}
                >
                {params.value}
                </Typography>
            </Box>
            )
        },
        { 
            field: 'totalBets', 
            headerName: isMobile ? 'Bets' : 'Total Bets', 
            width: isMobile ? 70 : 100, 
            type: 'number',
            minWidth: 60
        },
        { 
            field: 'totalPlayAmount', 
            headerName: isMobile ? 'Play' : 'Total Play', 
            width: isMobile ? 90 : 120, 
            type: 'number',
            minWidth: 80,
            valueFormatter: (value) => formatCurrency(value ?? 0)
        },
        { 
            field: 'totalWinnings', 
            headerName: isMobile ? 'Win' : 'Total Winnings', 
            width: isMobile ? 90 : 130, 
            type: 'number',
            minWidth: 80,
            valueFormatter: (value) => formatCurrency(value ?? 0)
        },
        { 
            field: 'netLoss', 
            headerName: isMobile ? 'Loss' : 'Net Loss', 
            width: isMobile ? 80 : 100, 
            type: 'number',
            minWidth: 70,
            valueFormatter: (value) => formatCurrency(value ?? 0),
            renderCell: (params) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {params.value > 0 ? (
                <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main' }} />
                ) : (
                <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main' }} />
                )}
                <Typography 
                variant="body2" 
                color={params.value > 0 ? 'error.main' : 'success.main'}
                sx={{ 
                    fontWeight: 'medium',
                    fontSize: { xs: '0.75rem', md: '0.875rem' }
                }}
                >
                {formatCurrency(params.value ?? 0)}
                </Typography>
            </Box>
            )
        },
        { 
            field: 'winRatePercentage', 
            headerName: isMobile ? 'Rate' : 'Win Rate', 
            width: isMobile ? 80 : 100, 
            type: 'number',
            minWidth: 70,
            valueFormatter: (value) => `${value?.toFixed(1) || '0.0'}%`,
            renderCell: (params) => (
            <Chip 
                label={`${params.value?.toFixed(1) || '0.0'}%`}
                color={params.value >= 50 ? 'success' : 'warning'}
                size="small"
                variant="outlined"
                sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
            />
            )
        }
    ];

    if (!isMobile) {
        baseColumns.push(
            { field: 'avgBetAmount', headerName: 'Avg Bet', width: 100, type: 'number', valueFormatter: (value) => formatCurrency(value ?? 0) },
            { field: 'uniqueGamesPlayed', headerName: 'Games', width: 80, type: 'number' }
        );
    }

    if (!isTablet) {
        baseColumns.push(
            { field: 'firstBetDate', headerName: 'First Bet', width: 120, type: 'dateTime', valueFormatter: (value) => value ? new Date(value).toLocaleDateString() : 'N/A' },
            { field: 'lastBetDate', headerName: 'Last Bet', width: 120, type: 'dateTime', valueFormatter: (value) => value ? new Date(value).toLocaleDateString() : 'N/A' }
        );
    }

    return baseColumns;
  }, [isMobile, isTablet, searchMode]);

  const summaryCards = [
    { title: 'Total Customers', value: summary.totalCustomers?.toLocaleString() || '0', color: 'primary.main', icon: <PeopleIcon />, bgColor: theme.palette.primary.light },
    { title: 'Total Play Amount', value: formatCurrency(summary.totalPlayAmount ?? 0), color: 'info.main', icon: <AccountBalanceIcon />, bgColor: theme.palette.info.light },
    { title: 'Total Winnings', value: formatCurrency(summary.totalWinnings ?? 0), color: 'success.main', icon: <EmojiEventsIcon />, bgColor: theme.palette.success.light },
    { title: 'Avg Customer Value', value: formatCurrency(summary.avgCustomerValue ?? 0), color: 'warning.main', icon: <AnalyticsIcon />, bgColor: theme.palette.warning.light }
  ];

  const LoadingSkeleton = () => (
    <Box>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[1, 2, 3, 4].map((index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card><CardContent><Skeleton variant="text" width="60%" height={32} /><Skeleton variant="text" width="40%" /></CardContent></Card>
          </Grid>
        ))}
      </Grid>
      <Paper sx={{ height: 400 }}><Skeleton variant="rectangular" height="100%" /></Paper>
    </Box>
  );

  if (isLoading && !searchMode) {
    return (
      <Container maxWidth="xl"><Box sx={{ py: 2 }}><Skeleton variant="text" width="200px" height={40} sx={{ mb: 1 }} /><Skeleton variant="text" width="300px" height={24} sx={{ mb: 3 }} /><LoadingSkeleton /></Box></Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 2 }}>
        {/* Header Section */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', md: 'center' },
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          mb: 3 
        }}>
          <Box>
            <Typography 
              variant={isMobile ? 'h5' : 'h4'}
              component="h1" 
              sx={{ fontWeight: 'bold', mb: 1 }}
            >
              Customers {searchMode && <Chip label="Search Results" color="primary" size="small" sx={{ ml: 1 }} />}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {searchMode ? `Search results for: ${searchQuery}` : 'All-time customer accounts and betting activity analysis'}
            </Typography>
          </Box>
          
          <Tooltip title="Refresh data">
            <IconButton onClick={() => refetch()} disabled={isFetching || isSearching} color="primary" sx={{ bgcolor: 'primary.light', '&:hover': { bgcolor: 'primary.main', color: 'white' } }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 3 }}>
          {summaryCards.map((card, index) => (
            <Grid item xs={6} sm={6} md={3} key={index}>
              <Card sx={{ 
                height: '100%', 
                background: `linear-gradient(135deg, ${alpha(card.bgColor, 0.2)} 0%, ${alpha(card.bgColor, 0.1)} 100%)`, 
                border: `1px solid ${alpha(card.bgColor, 0.4)}`, 
                transition: 'transform 0.2s, box-shadow 0.2s', 
                '&:hover': { transform: 'translateY(-2px)', boxShadow: theme.shadows[8] } 
              }}>
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Box sx={{ color: card.color, display: 'flex', fontSize: { xs: '1.2rem', md: '1.5rem' } }}>{card.icon}</Box>
                    <Typography component="div" sx={{ color: card.color, fontWeight: 'bold', fontSize: { xs: '1rem', md: '1.25rem' } }}>{card.value}</Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>{card.title}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Search Section - UPDATED with theme-aware styles */}
        <Card sx={{ 
          mb: 3, 
          background: `linear-gradient(135deg, ${alpha(theme.palette.background.default, 0.6)} 0%, ${alpha(theme.palette.primary.light, 0.15)} 100%)`,
          border: '1px solid',
          borderColor: 'divider',
          transition: 'transform 0.2s, box-shadow 0.2s', 
          '&:hover': { transform: 'translateY(-1px)', boxShadow: theme.shadows[4] }
        }}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
              <TextField
                fullWidth
                placeholder="Search by phone number..."
                value={searchInput}
                onChange={handleSearchInputChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: searchInput && (
                    <InputAdornment position="end">
                      <IconButton onClick={clearSearch} size="small">
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: theme.palette.background.paper, // Theme-aware background
                  }
                }}
              />
              
              {isSearching && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 'fit-content' }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">Searching...</Typography>
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Search Results Alert */}
        {searchMode && searchResults && !searchResults.found && (
          <Fade in>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">No customer found with phone number: <strong>{searchQuery}</strong></Typography>
              <Button onClick={clearSearch} size="small" sx={{ mt: 1 }}>View All Customers</Button>
            </Alert>
          </Fade>
        )}

        {searchMode && searchResults?.found && (
          <Fade in>
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="body2">✅ Found customer: <strong>{searchResults.customer.phone}</strong></Typography>
              <Button onClick={clearSearch} size="small" sx={{ mt: 1 }}>View All Customers</Button>
            </Alert>
          </Fade>
        )}

        {/* Error Handling */}
        {(error || searchError) && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Error loading customer data: {error?.message || searchError?.message || 'Unknown error'}
          </Alert>
        )}

        {/* Data Grid Section */}
        <Paper sx={{ height: { xs: 500, md: 600 }, width: '100%', overflow: 'hidden' }}>
          <DataGrid
            rows={rows}
            columns={getColumns()}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={isMobile ? [10, 25] : [25, 50, 100]}
            rowCount={searchMode ? rows.length : customersData?.summary.totalCustomers || 0}
            loading={isLoading || isFetching || isSearching}
            disableRowSelectionOnClick
            sx={{
              '& .MuiDataGrid-root': { border: 'none' },
              '& .MuiDataGrid-cell': { borderBottom: '1px solid', borderBottomColor: 'divider' },
              '& .MuiDataGrid-columnHeaders': { 
                backgroundColor: alpha(theme.palette.primary.light, 0.1), 
                borderBottom: '2px solid', 
                borderBottomColor: 'divider' 
              },
              '& .MuiDataGrid-row:hover': { backgroundColor: 'action.hover' },
              '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: alpha(theme.palette.action.hover, 0.5) },
              ...(isMobile && {
                '& .MuiDataGrid-columnHeader': { fontSize: '0.75rem', fontWeight: 'bold' },
                '& .MuiDataGrid-cell': { fontSize: '0.75rem', py: 0.5 },
              }),
            }}
            getRowId={(row) => row.id}
            density={isMobile ? 'compact' : 'standard'}
          />
        </Paper>

        {/* Data freshness indicator */}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: { xs: 'column', md: 'row' }, gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Data grouped by: {summary.groupedBy || 'phone'}
            {searchMode && <Chip label="Filtered" size="small" sx={{ ml: 1 }} />}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Showing {rows.length} customer{rows.length !== 1 ? 's' : ''} 
            {searchMode ? ` (Search: ${searchQuery})` : ' (All-Time Data)'}
          </Typography>
        </Box>

        {/* No customers found */}
        {!searchMode && !isLoading && customers.length === 0 && (
          <Alert severity="warning" sx={{ mt: 3 }}>
            No customer data available. This could be due to no betting activity or database connectivity issues.
          </Alert>
        )}
      </Box>
    </Container>
  );
};

export default Customers;

// import React from 'react';
// import { useSelector } from 'react-redux';
// import { 
//   Box, 
//   Typography, 
//   Paper, 
//   Grid, 
//   Card, 
//   CardContent,
//   Alert,
//   Chip,
//   CircularProgress,
//   useTheme,
//   useMediaQuery,
//   Skeleton,
//   Stack,
//   IconButton,
//   Tooltip,
//   Container,
//   TextField,
//   InputAdornment,
//   Button,
//   Fade,
//   Collapse
// } from '@mui/material';
// import { DataGrid } from '@mui/x-data-grid';
// import { 
//   Refresh as RefreshIcon,
//   TrendingUp as TrendingUpIcon,
//   TrendingDown as TrendingDownIcon,
//   People as PeopleIcon,
//   AccountBalance as AccountBalanceIcon,
//   EmojiEvents as EmojiEventsIcon,
//   Analytics as AnalyticsIcon,
//   Search as SearchIcon,
//   Clear as ClearIcon,
//   Phone as PhoneIcon
// } from '@mui/icons-material';
// import { selectCurrentFiltersAsApiParams } from '../../store/slices/dashboardSlice';
// import { useGetCustomersQuery, useLazySearchCustomerByPhoneQuery } from '../../store/api/dashboardApi';

// const Customers = () => {
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down('md'));
//   const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
//   // Search state
//   const [searchQuery, setSearchQuery] = React.useState('');
//   const [searchMode, setSearchMode] = React.useState(false);
//   const [searchResults, setSearchResults] = React.useState(null);
  
//   const allTimeFilters = {};
  
//   const [paginationModel, setPaginationModel] = React.useState({
//     page: 0,
//     pageSize: isMobile ? 10 : 25,
//   });

//   // Main customers query (for all customers)
//   const { 
//     data: customersData, 
//     isLoading, 
//     error,
//     refetch,
//     isFetching 
//   } = useGetCustomersQuery(searchMode ? { phone: searchQuery, limit: 100 } : allTimeFilters);

//   // Lazy query for phone search
//   const [
//     searchByPhone, 
//     { 
//       data: phoneSearchData, 
//       isLoading: isSearching, 
//       error: searchError 
//     }
//   ] = useLazySearchCustomerByPhoneQuery();

//   React.useEffect(() => {
//     setPaginationModel(prev => ({
//       ...prev,
//       pageSize: isMobile ? 10 : 25
//     }));
//   }, [isMobile]);

//   // Handle search
//   const handleSearch = React.useCallback((query) => {
//     const trimmedQuery = query.trim();
    
//     if (!trimmedQuery) {
//       setSearchMode(false);
//       setSearchResults(null);
//       setSearchQuery('');
//       return;
//     }

//     setSearchQuery(trimmedQuery);
//     setSearchMode(true);
    
//     // Use the lazy query for dedicated phone search
//     searchByPhone(trimmedQuery);
//   }, [searchByPhone]);

//   // Handle search input change with debounce
//   const [searchInput, setSearchInput] = React.useState('');
//   const searchTimeoutRef = React.useRef();

//   const handleSearchInputChange = (event) => {
//     const value = event.target.value;
//     setSearchInput(value);
    
//     // Clear existing timeout
//     if (searchTimeoutRef.current) {
//       clearTimeout(searchTimeoutRef.current);
//     }
    
//     // Set new timeout for debounced search
//     searchTimeoutRef.current = setTimeout(() => {
//       if (value.trim()) {
//         handleSearch(value);
//       } else {
//         clearSearch();
//       }
//     }, 500); // 500ms debounce
//   };

//   const clearSearch = () => {
//     setSearchInput('');
//     setSearchQuery('');
//     setSearchMode(false);
//     setSearchResults(null);
//     if (searchTimeoutRef.current) {
//       clearTimeout(searchTimeoutRef.current);
//     }
//   };

//   // Handle search results
//   React.useEffect(() => {
//     if (phoneSearchData) {
//       setSearchResults(phoneSearchData);
//     }
//   }, [phoneSearchData]);

//   // Debug logging
//   React.useEffect(() => {
//     console.log('Customers Query State:', {
//       isLoading,
//       error,
//       customersData,
//       searchMode,
//       searchQuery,
//       searchResults,
//       filters: searchMode ? { phone: searchQuery } : allTimeFilters
//     });
    
//     if (customersData) {
//       console.log('Summary data:', customersData.summary);
//       console.log('Customers array length:', customersData.customers?.length);
//     }
//   }, [isLoading, error, customersData, searchMode, searchQuery, searchResults, allTimeFilters]);

//   // Get data based on search mode
//   const getDisplayData = () => {
//     if (searchMode && searchResults) {
//       if (searchResults.found) {
//         return {
//           summary: {
//             totalCustomers: 1,
//             totalPlayAmount: searchResults.customer.totalPlayAmount,
//             totalWinnings: searchResults.customer.totalWinnings,
//             avgCustomerValue: searchResults.customer.totalPlayAmount,
//             groupedBy: 'phone'
//           },
//           customers: [searchResults.customer]
//         };
//       } else {
//         return {
//           summary: {
//             totalCustomers: 0,
//             totalPlayAmount: 0,
//             totalWinnings: 0,
//             avgCustomerValue: 0,
//             groupedBy: 'phone'
//           },
//           customers: []
//         };
//       }
//     }
//     return customersData;
//   };

//   const displayData = getDisplayData();
//   const summary = displayData?.summary || {};
//   const customers = displayData?.customers || [];

//   // Data transformation for DataGrid
//   const rows = React.useMemo(() => {
//     console.log('Processing customers for DataGrid:', customers);
    
//     return customers.map((customer, index) => ({
//       id: customer.userId || customer.phone || `customer-${index}`,
//       phone: customer.phone,
//       userId: customer.userId,
//       totalBets: customer.totalBets,
//       totalPlayAmount: customer.totalPlayAmount,
//       totalWinnings: customer.totalWinnings,
//       netLoss: customer.netLoss,
//       avgBetAmount: customer.avgBetAmount,
//       firstBetDate: customer.firstBetDate ? new Date(customer.firstBetDate) : null,
//       lastBetDate: customer.lastBetDate ? new Date(customer.lastBetDate) : null,
//       uniqueGamesPlayed: customer.uniqueGamesPlayed,
//       gamesList: customer.gamesList,
//       winningBets: customer.winningBets,
//       winRatePercentage: customer.winRatePercentage,
//       filteredService: customer.filteredService,
//     }));
//   }, [customers]);

//   // Responsive column configuration
//   const getColumns = React.useCallback(() => {
//     const baseColumns = [
//       { 
//         field: 'phone', 
//         headerName: 'Phone', 
//         width: 140,
//         minWidth: 120,
//         flex: 1,
//         renderCell: (params) => (
//           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//             <PhoneIcon sx={{ fontSize: 16, color: 'primary.main' }} />
//             <Typography 
//               variant="body2" 
//               sx={{ 
//                 fontFamily: 'monospace',
//                 fontSize: { xs: '0.75rem', md: '0.875rem' },
//                 fontWeight: searchMode ? 'bold' : 'normal',
//                 color: searchMode ? 'primary.main' : 'inherit'
//               }}
//             >
//               {params.value}
//             </Typography>
//           </Box>
//         )
//       },
//       { 
//         field: 'totalBets', 
//         headerName: isMobile ? 'Bets' : 'Total Bets', 
//         width: isMobile ? 70 : 100, 
//         type: 'number',
//         minWidth: 60
//       },
//       { 
//         field: 'totalPlayAmount', 
//         headerName: isMobile ? 'Play' : 'Total Play', 
//         width: isMobile ? 90 : 120, 
//         type: 'number',
//         minWidth: 80,
//         valueFormatter: (value) => `$${value?.toFixed(2) || '0.00'}`
//       },
//       { 
//         field: 'totalWinnings', 
//         headerName: isMobile ? 'Win' : 'Total Winnings', 
//         width: isMobile ? 90 : 130, 
//         type: 'number',
//         minWidth: 80,
//         valueFormatter: (value) => `$${value?.toFixed(2) || '0.00'}`
//       },
//       { 
//         field: 'netLoss', 
//         headerName: isMobile ? 'Loss' : 'Net Loss', 
//         width: isMobile ? 80 : 100, 
//         type: 'number',
//         minWidth: 70,
//         valueFormatter: (value) => `$${value?.toFixed(2) || '0.00'}`,
//         renderCell: (params) => (
//           <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
//             {params.value > 0 ? (
//               <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main' }} />
//             ) : (
//               <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main' }} />
//             )}
//             <Typography 
//               variant="body2" 
//               color={params.value > 0 ? 'error.main' : 'success.main'}
//               sx={{ 
//                 fontWeight: 'medium',
//                 fontSize: { xs: '0.75rem', md: '0.875rem' }
//               }}
//             >
//               ${params.value?.toFixed(2) || '0.00'}
//             </Typography>
//           </Box>
//         )
//       },
//       { 
//         field: 'winRatePercentage', 
//         headerName: isMobile ? 'Rate' : 'Win Rate', 
//         width: isMobile ? 80 : 100, 
//         type: 'number',
//         minWidth: 70,
//         valueFormatter: (value) => `${value?.toFixed(1) || '0.0'}%`,
//         renderCell: (params) => (
//           <Chip 
//             label={`${params.value?.toFixed(1) || '0.0'}%`}
//             color={params.value >= 50 ? 'success' : 'warning'}
//             size="small"
//             variant="outlined"
//             sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
//           />
//         )
//       }
//     ];

//     if (!isMobile) {
//       baseColumns.push(
//         { field: 'avgBetAmount', headerName: 'Avg Bet', width: 100, type: 'number', valueFormatter: (value) => `$${value?.toFixed(2) || '0.00'}` },
//         { field: 'uniqueGamesPlayed', headerName: 'Games', width: 80, type: 'number' }
//       );
//     }

//     if (!isTablet) {
//       baseColumns.push(
//         { field: 'firstBetDate', headerName: 'First Bet', width: 120, type: 'dateTime', valueFormatter: (value) => value ? new Date(value).toLocaleDateString() : 'N/A' },
//         { field: 'lastBetDate', headerName: 'Last Bet', width: 120, type: 'dateTime', valueFormatter: (value) => value ? new Date(value).toLocaleDateString() : 'N/A' }
//       );
//     }

//     return baseColumns;
//   }, [isMobile, isTablet, searchMode]);

//   const summaryCards = [
//     { title: 'Total Customers', value: summary.totalCustomers?.toLocaleString() || '0', color: 'primary.main', icon: <PeopleIcon />, bgColor: 'primary.light' },
//     { title: 'Total Play Amount', value: `$${summary.totalPlayAmount?.toLocaleString() || '0.00'}`, color: 'info.main', icon: <AccountBalanceIcon />, bgColor: 'info.light' },
//     { title: 'Total Winnings', value: `$${summary.totalWinnings?.toLocaleString() || '0.00'}`, color: 'success.main', icon: <EmojiEventsIcon />, bgColor: 'success.light' },
//     { title: 'Avg Customer Value', value: `$${summary.avgCustomerValue?.toFixed(2) || '0.00'}`, color: 'warning.main', icon: <AnalyticsIcon />, bgColor: 'warning.light' }
//   ];

//   const LoadingSkeleton = () => (
//     <Box>
//       <Grid container spacing={3} sx={{ mb: 3 }}>
//         {[1, 2, 3, 4].map((index) => (
//           <Grid item xs={12} sm={6} md={3} key={index}>
//             <Card><CardContent><Skeleton variant="text" width="60%" height={32} /><Skeleton variant="text" width="40%" /></CardContent></Card>
//           </Grid>
//         ))}
//       </Grid>
//       <Paper sx={{ height: 400 }}><Skeleton variant="rectangular" height="100%" /></Paper>
//     </Box>
//   );

//   if (isLoading && !searchMode) {
//     return (
//       <Container maxWidth="xl"><Box sx={{ py: 2 }}><Skeleton variant="text" width="200px" height={40} sx={{ mb: 1 }} /><Skeleton variant="text" width="300px" height={24} sx={{ mb: 3 }} /><LoadingSkeleton /></Box></Container>
//     );
//   }

//   return (
//     <Container maxWidth="xl">
//       <Box sx={{ py: 2 }}>
//         {/* Header Section */}
//         <Box sx={{ 
//           display: 'flex', 
//           justifyContent: 'space-between', 
//           alignItems: { xs: 'flex-start', md: 'center' },
//           flexDirection: { xs: 'column', md: 'row' },
//           gap: 2,
//           mb: 3 
//         }}>
//           <Box>
//             <Typography 
//               variant={isMobile ? 'h5' : 'h4'}
//               component="h1" 
//               sx={{ fontWeight: 'bold', mb: 1 }}
//             >
//               Customers {searchMode && <Chip label="Search Results" color="primary" size="small" sx={{ ml: 1 }} />}
//             </Typography>
//             <Typography variant="body1" color="text.secondary">
//               {searchMode ? `Search results for: ${searchQuery}` : 'All-time customer accounts and betting activity analysis'}
//             </Typography>
//           </Box>
          
//           <Tooltip title="Refresh data">
//             <IconButton onClick={() => refetch()} disabled={isFetching || isSearching} color="primary" sx={{ bgcolor: 'primary.light', '&:hover': { bgcolor: 'primary.main', color: 'white' } }}>
//               <RefreshIcon />
//             </IconButton>
//           </Tooltip>
//         </Box>

//         {/* Summary Cards */}
//         <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 3 }}>
//           {summaryCards.map((card, index) => (
//             <Grid item xs={6} sm={6} md={3} key={index}>
//               <Card sx={{ 
//                 height: '100%', 
//                 background: `linear-gradient(135deg, ${card.bgColor}20 0%, ${card.bgColor}10 100%)`, 
//                 border: `1px solid ${card.bgColor}40`, 
//                 transition: 'transform 0.2s, box-shadow 0.2s', 
//                 '&:hover': { transform: 'translateY(-2px)', boxShadow: theme.shadows[8] } 
//               }}>
//                 <CardContent sx={{ p: { xs: 2, md: 3 } }}>
//                   <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
//                     <Box sx={{ color: card.color, display: 'flex', fontSize: { xs: '1.2rem', md: '1.5rem' } }}>{card.icon}</Box>
//                     <Typography component="div" sx={{ color: card.color, fontWeight: 'bold', fontSize: { xs: '1rem', md: '1.25rem' } }}>{card.value}</Typography>
//                   </Stack>
//                   <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>{card.title}</Typography>
//                 </CardContent>
//               </Card>
//             </Grid>
//           ))}
//         </Grid>

//         {/* Search Section - Moved below summary cards */}
//         <Card sx={{ 
//           mb: 3, 
//           background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
//           border: '1px solid',
//           borderColor: 'primary.light',
//           transition: 'transform 0.2s, box-shadow 0.2s', 
//           '&:hover': { transform: 'translateY(-1px)', boxShadow: theme.shadows[4] }
//         }}>
//           <CardContent sx={{ p: { xs: 2, md: 3 } }}>
//             <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
//               <TextField
//                 fullWidth
//                 placeholder="Search by phone number (e.g., +1234567890, 123-456-7890)"
//                 value={searchInput}
//                 onChange={handleSearchInputChange}
//                 InputProps={{
//                   startAdornment: (
//                     <InputAdornment position="start">
//                       <SearchIcon color="action" />
//                     </InputAdornment>
//                   ),
//                   endAdornment: searchInput && (
//                     <InputAdornment position="end">
//                       <IconButton onClick={clearSearch} size="small">
//                         <ClearIcon />
//                       </IconButton>
//                     </InputAdornment>
//                   )
//                 }}
//                 sx={{
//                   '& .MuiOutlinedInput-root': {
//                     bgcolor: 'white',
//                     '&:hover': { bgcolor: 'grey.50' },
//                     '&.Mui-focused': { bgcolor: 'white' }
//                   }
//                 }}
//               />
              
//               {isSearching && (
//                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 'fit-content' }}>
//                   <CircularProgress size={20} />
//                   <Typography variant="body2">Searching...</Typography>
//                 </Box>
//               )}
//             </Stack>

//             {/* Search Help */}
//             <Collapse in={!searchInput}>
//               <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
//                 💡 Tip: Enter a phone number to find specific customer betting activity and statistics
//               </Typography>
//             </Collapse>
//           </CardContent>
//         </Card>

//         {/* Search Results Alert */}
//         {searchMode && searchResults && !searchResults.found && (
//           <Fade in>
//             <Alert severity="info" sx={{ mb: 3 }}>
//               <Typography variant="body2">
//                 No customer found with phone number: <strong>{searchQuery}</strong>
//               </Typography>
//               <Button onClick={clearSearch} size="small" sx={{ mt: 1 }}>
//                 View All Customers
//               </Button>
//             </Alert>
//           </Fade>
//         )}

//         {searchMode && searchResults?.found && (
//           <Fade in>
//             <Alert severity="success" sx={{ mb: 3 }}>
//               <Typography variant="body2">
//                 ✅ Found customer: <strong>{searchResults.customer.phone}</strong>
//               </Typography>
//               <Button onClick={clearSearch} size="small" sx={{ mt: 1 }}>
//                 View All Customers
//               </Button>
//             </Alert>
//           </Fade>
//         )}

//         {/* Error Handling */}
//         {(error || searchError) && (
//           <Alert severity="error" sx={{ mb: 3 }}>
//             Error loading customer data: {error?.message || searchError?.message || 'Unknown error'}
//           </Alert>
//         )}

//         {/* Loading indicator for refresh */}
//         {(isFetching || isSearching) && !isLoading && (
//           <Box display="flex" justifyContent="center" alignItems="center" sx={{ mb: 2 }}>
//             <CircularProgress size={20} />
//             <Typography variant="body2" sx={{ ml: 1 }}>
//               {isSearching ? 'Searching...' : 'Refreshing...'}
//             </Typography>
//           </Box>
//         )}

//         {/* Data Grid Section */}
//         {!isLoading && customers.length > 0 && (
//           <>
//             <Paper sx={{ height: { xs: 500, md: 600 }, width: '100%', overflow: 'hidden' }}>
//               <DataGrid
//                 rows={rows}
//                 columns={getColumns()}
//                 paginationModel={paginationModel}
//                 onPaginationModelChange={setPaginationModel}
//                 pageSizeOptions={isMobile ? [10, 25] : [25, 50, 100]}
//                 rowCount={rows.length}
//                 loading={isLoading || isFetching || isSearching}
//                 disableRowSelectionOnClick
//                 sx={{
//                   '& .MuiDataGrid-root': { border: 'none' },
//                   '& .MuiDataGrid-cell': { borderBottom: '1px solid', borderBottomColor: 'divider', py: 1 },
//                   '& .MuiDataGrid-columnHeaders': { backgroundColor: 'grey.50', borderBottom: '2px solid', borderBottomColor: 'divider' },
//                   '& .MuiDataGrid-row:hover': { backgroundColor: 'action.hover' },
//                   '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: 'grey.25' },
//                   ...(isMobile && {
//                     '& .MuiDataGrid-columnHeader': { fontSize: '0.75rem', fontWeight: 'bold' },
//                     '& .MuiDataGrid-cell': { fontSize: '0.75rem', py: 0.5 },
//                   }),
//                 }}
//                 initialState={{
//                   columns: {
//                     columnVisibilityModel: {
//                       userId: false,
//                       gamesList: false,
//                       ...(isMobile && { avgBetAmount: false, uniqueGamesPlayed: false, firstBetDate: false, lastBetDate: false }),
//                     },
//                   },
//                 }}
//                 getRowId={(row) => row.id}
//                 density={isMobile ? 'compact' : 'standard'}
//               />
//             </Paper>

//             {/* Data freshness indicator */}
//             <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: { xs: 'column', md: 'row' }, gap: 1 }}>
//               <Typography variant="caption" color="text.secondary">
//                 Data grouped by: {summary.groupedBy || 'phone'}
//                 {searchMode && <Chip label="Filtered" size="small" sx={{ ml: 1 }} />}
//               </Typography>
//               <Typography variant="caption" color="text.secondary">
//                 Showing {rows.length} customer{rows.length !== 1 ? 's' : ''} 
//                 {searchMode ? ` (Search: ${searchQuery})` : ' (All-Time Data)'}
//               </Typography>
//             </Box>
//           </>
//         )}

//         {/* No customers found */}
//         {!isLoading && customers.length === 0 && !searchMode && (
//           <Alert severity="warning" sx={{ mb: 3 }}>
//             No customer data available. This could be due to no betting activity or database connectivity issues.
//           </Alert>
//         )}
//       </Box>
//     </Container>
//   );
// };

// export default Customers;

// import React from 'react';
// import { useSelector } from 'react-redux';
// import { 
//   Box, 
//   Typography, 
//   Paper, 
//   Grid, 
//   Card, 
//   CardContent,
//   Alert,
//   Chip,
//   CircularProgress,
//   useTheme,
//   useMediaQuery,
//   Skeleton,
//   Stack,
//   IconButton,
//   Tooltip,
//   Container,
//   TextField,
//   InputAdornment,
//   Button,
//   Fade,
//   Collapse
// } from '@mui/material';
// import { DataGrid } from '@mui/x-data-grid';
// import { 
//   Refresh as RefreshIcon,
//   TrendingUp as TrendingUpIcon,
//   TrendingDown as TrendingDownIcon,
//   People as PeopleIcon,
//   AccountBalance as AccountBalanceIcon,
//   EmojiEvents as EmojiEventsIcon,
//   Analytics as AnalyticsIcon,
//   Search as SearchIcon,
//   Clear as ClearIcon,
//   Phone as PhoneIcon
// } from '@mui/icons-material';
// import { selectCurrentFiltersAsApiParams } from '../../store/slices/dashboardSlice';
// import { useGetCustomersQuery, useLazySearchCustomerByPhoneQuery } from '../../store/api/dashboardApi';

// const Customers = () => {
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down('md'));
//   const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
//   // Search state
//   const [searchQuery, setSearchQuery] = React.useState('');
//   const [searchMode, setSearchMode] = React.useState(false);
//   const [searchResults, setSearchResults] = React.useState(null);
  
//   const allTimeFilters = {};
  
//   const [paginationModel, setPaginationModel] = React.useState({
//     page: 0,
//     pageSize: isMobile ? 10 : 25,
//   });

//   // Main customers query (for all customers)
//   const { 
//     data: customersData, 
//     isLoading, 
//     error,
//     refetch,
//     isFetching 
//   } = useGetCustomersQuery(searchMode ? { phone: searchQuery, limit: 100 } : allTimeFilters);

//   // Lazy query for phone search
//   const [
//     searchByPhone, 
//     { 
//       data: phoneSearchData, 
//       isLoading: isSearching, 
//       error: searchError 
//     }
//   ] = useLazySearchCustomerByPhoneQuery();

//   React.useEffect(() => {
//     setPaginationModel(prev => ({
//       ...prev,
//       pageSize: isMobile ? 10 : 25
//     }));
//   }, [isMobile]);

//   // Handle search
//   const handleSearch = React.useCallback((query) => {
//     const trimmedQuery = query.trim();
    
//     if (!trimmedQuery) {
//       setSearchMode(false);
//       setSearchResults(null);
//       setSearchQuery('');
//       return;
//     }

//     setSearchQuery(trimmedQuery);
//     setSearchMode(true);
    
//     // Use the lazy query for dedicated phone search
//     searchByPhone(trimmedQuery);
//   }, [searchByPhone]);

//   // Handle search input change with debounce
//   const [searchInput, setSearchInput] = React.useState('');
//   const searchTimeoutRef = React.useRef();

//   const handleSearchInputChange = (event) => {
//     const value = event.target.value;
//     setSearchInput(value);
    
//     // Clear existing timeout
//     if (searchTimeoutRef.current) {
//       clearTimeout(searchTimeoutRef.current);
//     }
    
//     // Set new timeout for debounced search
//     searchTimeoutRef.current = setTimeout(() => {
//       if (value.trim()) {
//         handleSearch(value);
//       } else {
//         clearSearch();
//       }
//     }, 500); // 500ms debounce
//   };

//   const clearSearch = () => {
//     setSearchInput('');
//     setSearchQuery('');
//     setSearchMode(false);
//     setSearchResults(null);
//     if (searchTimeoutRef.current) {
//       clearTimeout(searchTimeoutRef.current);
//     }
//   };

//   // Handle search results
//   React.useEffect(() => {
//     if (phoneSearchData) {
//       setSearchResults(phoneSearchData);
//     }
//   }, [phoneSearchData]);

//   // Debug logging
//   React.useEffect(() => {
//     console.log('Customers Query State:', {
//       isLoading,
//       error,
//       customersData,
//       searchMode,
//       searchQuery,
//       searchResults,
//       filters: searchMode ? { phone: searchQuery } : allTimeFilters
//     });
    
//     if (customersData) {
//       console.log('Summary data:', customersData.summary);
//       console.log('Customers array length:', customersData.customers?.length);
//     }
//   }, [isLoading, error, customersData, searchMode, searchQuery, searchResults, allTimeFilters]);

//   // Get data based on search mode
//   const getDisplayData = () => {
//     if (searchMode && searchResults) {
//       if (searchResults.found) {
//         return {
//           summary: {
//             totalCustomers: 1,
//             totalPlayAmount: searchResults.customer.totalPlayAmount,
//             totalWinnings: searchResults.customer.totalWinnings,
//             avgCustomerValue: searchResults.customer.totalPlayAmount,
//             groupedBy: 'phone'
//           },
//           customers: [searchResults.customer]
//         };
//       } else {
//         return {
//           summary: {
//             totalCustomers: 0,
//             totalPlayAmount: 0,
//             totalWinnings: 0,
//             avgCustomerValue: 0,
//             groupedBy: 'phone'
//           },
//           customers: []
//         };
//       }
//     }
//     return customersData;
//   };

//   const displayData = getDisplayData();
//   const summary = displayData?.summary || {};
//   const customers = displayData?.customers || [];

//   // Data transformation for DataGrid
//   const rows = React.useMemo(() => {
//     console.log('Processing customers for DataGrid:', customers);
    
//     return customers.map((customer, index) => ({
//       id: customer.userId || customer.phone || `customer-${index}`,
//       phone: customer.phone,
//       userId: customer.userId,
//       totalBets: customer.totalBets,
//       totalPlayAmount: customer.totalPlayAmount,
//       totalWinnings: customer.totalWinnings,
//       netLoss: customer.netLoss,
//       avgBetAmount: customer.avgBetAmount,
//       firstBetDate: customer.firstBetDate ? new Date(customer.firstBetDate) : null,
//       lastBetDate: customer.lastBetDate ? new Date(customer.lastBetDate) : null,
//       uniqueGamesPlayed: customer.uniqueGamesPlayed,
//       gamesList: customer.gamesList,
//       winningBets: customer.winningBets,
//       winRatePercentage: customer.winRatePercentage,
//       filteredService: customer.filteredService,
//     }));
//   }, [customers]);

//   // Responsive column configuration
//   const getColumns = React.useCallback(() => {
//     const baseColumns = [
//       { 
//         field: 'phone', 
//         headerName: 'Phone', 
//         width: 140,
//         minWidth: 120,
//         flex: 1,
//         renderCell: (params) => (
//           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//             <PhoneIcon sx={{ fontSize: 16, color: 'primary.main' }} />
//             <Typography 
//               variant="body2" 
//               sx={{ 
//                 fontFamily: 'monospace',
//                 fontSize: { xs: '0.75rem', md: '0.875rem' },
//                 fontWeight: searchMode ? 'bold' : 'normal',
//                 color: searchMode ? 'primary.main' : 'inherit'
//               }}
//             >
//               {params.value}
//             </Typography>
//           </Box>
//         )
//       },
//       { 
//         field: 'totalBets', 
//         headerName: isMobile ? 'Bets' : 'Total Bets', 
//         width: isMobile ? 70 : 100, 
//         type: 'number',
//         minWidth: 60
//       },
//       { 
//         field: 'totalPlayAmount', 
//         headerName: isMobile ? 'Play' : 'Total Play', 
//         width: isMobile ? 90 : 120, 
//         type: 'number',
//         minWidth: 80,
//         valueFormatter: (value) => `$${value?.toFixed(2) || '0.00'}`
//       },
//       { 
//         field: 'totalWinnings', 
//         headerName: isMobile ? 'Win' : 'Total Winnings', 
//         width: isMobile ? 90 : 130, 
//         type: 'number',
//         minWidth: 80,
//         valueFormatter: (value) => `$${value?.toFixed(2) || '0.00'}`
//       },
//       { 
//         field: 'netLoss', 
//         headerName: isMobile ? 'Loss' : 'Net Loss', 
//         width: isMobile ? 80 : 100, 
//         type: 'number',
//         minWidth: 70,
//         valueFormatter: (value) => `$${value?.toFixed(2) || '0.00'}`,
//         renderCell: (params) => (
//           <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
//             {params.value > 0 ? (
//               <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main' }} />
//             ) : (
//               <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main' }} />
//             )}
//             <Typography 
//               variant="body2" 
//               color={params.value > 0 ? 'error.main' : 'success.main'}
//               sx={{ 
//                 fontWeight: 'medium',
//                 fontSize: { xs: '0.75rem', md: '0.875rem' }
//               }}
//             >
//               ${params.value?.toFixed(2) || '0.00'}
//             </Typography>
//           </Box>
//         )
//       },
//       { 
//         field: 'winRatePercentage', 
//         headerName: isMobile ? 'Rate' : 'Win Rate', 
//         width: isMobile ? 80 : 100, 
//         type: 'number',
//         minWidth: 70,
//         valueFormatter: (value) => `${value?.toFixed(1) || '0.0'}%`,
//         renderCell: (params) => (
//           <Chip 
//             label={`${params.value?.toFixed(1) || '0.0'}%`}
//             color={params.value >= 50 ? 'success' : 'warning'}
//             size="small"
//             variant="outlined"
//             sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
//           />
//         )
//       }
//     ];

//     if (!isMobile) {
//       baseColumns.push(
//         { field: 'avgBetAmount', headerName: 'Avg Bet', width: 100, type: 'number', valueFormatter: (value) => `$${value?.toFixed(2) || '0.00'}` },
//         { field: 'uniqueGamesPlayed', headerName: 'Games', width: 80, type: 'number' }
//       );
//     }

//     if (!isTablet) {
//       baseColumns.push(
//         { field: 'firstBetDate', headerName: 'First Bet', width: 120, type: 'dateTime', valueFormatter: (value) => value ? new Date(value).toLocaleDateString() : 'N/A' },
//         { field: 'lastBetDate', headerName: 'Last Bet', width: 120, type: 'dateTime', valueFormatter: (value) => value ? new Date(value).toLocaleDateString() : 'N/A' }
//       );
//     }

//     return baseColumns;
//   }, [isMobile, isTablet, searchMode]);

//   const summaryCards = [
//     { title: 'Total Customers', value: summary.totalCustomers?.toLocaleString() || '0', color: 'primary.main', icon: <PeopleIcon />, bgColor: 'primary.light' },
//     { title: 'Total Play Amount', value: `$${summary.totalPlayAmount?.toLocaleString() || '0.00'}`, color: 'info.main', icon: <AccountBalanceIcon />, bgColor: 'info.light' },
//     { title: 'Total Winnings', value: `$${summary.totalWinnings?.toLocaleString() || '0.00'}`, color: 'success.main', icon: <EmojiEventsIcon />, bgColor: 'success.light' },
//     { title: 'Avg Customer Value', value: `$${summary.avgCustomerValue?.toFixed(2) || '0.00'}`, color: 'warning.main', icon: <AnalyticsIcon />, bgColor: 'warning.light' }
//   ];

//   const LoadingSkeleton = () => (
//     <Box>
//       <Grid container spacing={3} sx={{ mb: 3 }}>
//         {[1, 2, 3, 4].map((index) => (
//           <Grid item xs={12} sm={6} md={3} key={index}>
//             <Card><CardContent><Skeleton variant="text" width="60%" height={32} /><Skeleton variant="text" width="40%" /></CardContent></Card>
//           </Grid>
//         ))}
//       </Grid>
//       <Paper sx={{ height: 400 }}><Skeleton variant="rectangular" height="100%" /></Paper>
//     </Box>
//   );

//   if (isLoading && !searchMode) {
//     return (
//       <Container maxWidth="xl"><Box sx={{ py: 2 }}><Skeleton variant="text" width="200px" height={40} sx={{ mb: 1 }} /><Skeleton variant="text" width="300px" height={24} sx={{ mb: 3 }} /><LoadingSkeleton /></Box></Container>
//     );
//   }

//   return (
//     <Container maxWidth="xl">
//       <Box sx={{ py: 2 }}>
//         {/* Header Section */}
//         <Box sx={{ 
//           display: 'flex', 
//           justifyContent: 'space-between', 
//           alignItems: { xs: 'flex-start', md: 'center' },
//           flexDirection: { xs: 'column', md: 'row' },
//           gap: 2,
//           mb: 3 
//         }}>
//           <Box>
//             <Typography 
//               variant={isMobile ? 'h5' : 'h4'}
//               component="h1" 
//               sx={{ fontWeight: 'bold', mb: 1 }}
//             >
//               Customers {searchMode && <Chip label="Search Results" color="primary" size="small" sx={{ ml: 1 }} />}
//             </Typography>
//             <Typography variant="body1" color="text.secondary">
//               {searchMode ? `Search results for: ${searchQuery}` : 'All-time customer accounts and betting activity analysis'}
//             </Typography>
//           </Box>
          
//           <Tooltip title="Refresh data">
//             <IconButton onClick={() => refetch()} disabled={isFetching || isSearching} color="primary" sx={{ bgcolor: 'primary.light', '&:hover': { bgcolor: 'primary.main', color: 'white' } }}>
//               <RefreshIcon />
//             </IconButton>
//           </Tooltip>
//         </Box>

//         {/* Search Section */}
//         <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
//           <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
//             <TextField
//               fullWidth
//               placeholder="Search by phone number (e.g., +1234567890, 123-456-7890)"
//               value={searchInput}
//               onChange={handleSearchInputChange}
//               InputProps={{
//                 startAdornment: (
//                   <InputAdornment position="start">
//                     <SearchIcon color="action" />
//                   </InputAdornment>
//                 ),
//                 endAdornment: searchInput && (
//                   <InputAdornment position="end">
//                     <IconButton onClick={clearSearch} size="small">
//                       <ClearIcon />
//                     </IconButton>
//                   </InputAdornment>
//                 )
//               }}
//               sx={{
//                 '& .MuiOutlinedInput-root': {
//                   bgcolor: 'white',
//                   '&:hover': { bgcolor: 'grey.50' }
//                 }
//               }}
//             />
            
//             {isSearching && (
//               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 'fit-content' }}>
//                 <CircularProgress size={20} />
//                 <Typography variant="body2">Searching...</Typography>
//               </Box>
//             )}
//           </Stack>

//           {/* Search Help */}
//           <Collapse in={!searchInput}>
//             <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
//               💡 Tip: Enter a phone number to find specific customer betting activity and statistics
//             </Typography>
//           </Collapse>
//         </Paper>

//         {/* Search Results Alert */}
//         {searchMode && searchResults && !searchResults.found && (
//           <Fade in>
//             <Alert severity="info" sx={{ mb: 3 }}>
//               <Typography variant="body2">
//                 No customer found with phone number: <strong>{searchQuery}</strong>
//               </Typography>
//               <Button onClick={clearSearch} size="small" sx={{ mt: 1 }}>
//                 View All Customers
//               </Button>
//             </Alert>
//           </Fade>
//         )}

//         {searchMode && searchResults?.found && (
//           <Fade in>
//             <Alert severity="success" sx={{ mb: 3 }}>
//               <Typography variant="body2">
//                 ✅ Found customer: <strong>{searchResults.customer.phone}</strong>
//               </Typography>
//               <Button onClick={clearSearch} size="small" sx={{ mt: 1 }}>
//                 View All Customers
//               </Button>
//             </Alert>
//           </Fade>
//         )}

//         {/* Error Handling */}
//         {(error || searchError) && (
//           <Alert severity="error" sx={{ mb: 3 }}>
//             Error loading customer data: {error?.message || searchError?.message || 'Unknown error'}
//           </Alert>
//         )}

//         {/* Loading indicator for refresh */}
//         {(isFetching || isSearching) && !isLoading && (
//           <Box display="flex" justifyContent="center" alignItems="center" sx={{ mb: 2 }}>
//             <CircularProgress size={20} />
//             <Typography variant="body2" sx={{ ml: 1 }}>
//               {isSearching ? 'Searching...' : 'Refreshing...'}
//             </Typography>
//           </Box>
//         )}

//         {/* Summary Cards */}
//         <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 3 }}>
//           {summaryCards.map((card, index) => (
//             <Grid item xs={6} sm={6} md={3} key={index}>
//               <Card sx={{ 
//                 height: '100%', 
//                 background: `linear-gradient(135deg, ${card.bgColor}20 0%, ${card.bgColor}10 100%)`, 
//                 border: `1px solid ${card.bgColor}40`, 
//                 transition: 'transform 0.2s, box-shadow 0.2s', 
//                 '&:hover': { transform: 'translateY(-2px)', boxShadow: theme.shadows[8] } 
//               }}>
//                 <CardContent sx={{ p: { xs: 2, md: 3 } }}>
//                   <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
//                     <Box sx={{ color: card.color, display: 'flex', fontSize: { xs: '1.2rem', md: '1.5rem' } }}>{card.icon}</Box>
//                     <Typography component="div" sx={{ color: card.color, fontWeight: 'bold', fontSize: { xs: '1rem', md: '1.25rem' } }}>{card.value}</Typography>
//                   </Stack>
//                   <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>{card.title}</Typography>
//                 </CardContent>
//               </Card>
//             </Grid>
//           ))}
//         </Grid>

//         {/* Data Grid Section */}
//         {!isLoading && customers.length > 0 && (
//           <>
//             <Paper sx={{ height: { xs: 500, md: 600 }, width: '100%', overflow: 'hidden' }}>
//               <DataGrid
//                 rows={rows}
//                 columns={getColumns()}
//                 paginationModel={paginationModel}
//                 onPaginationModelChange={setPaginationModel}
//                 pageSizeOptions={isMobile ? [10, 25] : [25, 50, 100]}
//                 rowCount={rows.length}
//                 loading={isLoading || isFetching || isSearching}
//                 disableRowSelectionOnClick
//                 sx={{
//                   '& .MuiDataGrid-root': { border: 'none' },
//                   '& .MuiDataGrid-cell': { borderBottom: '1px solid', borderBottomColor: 'divider', py: 1 },
//                   '& .MuiDataGrid-columnHeaders': { backgroundColor: 'grey.50', borderBottom: '2px solid', borderBottomColor: 'divider' },
//                   '& .MuiDataGrid-row:hover': { backgroundColor: 'action.hover' },
//                   '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: 'grey.25' },
//                   ...(isMobile && {
//                     '& .MuiDataGrid-columnHeader': { fontSize: '0.75rem', fontWeight: 'bold' },
//                     '& .MuiDataGrid-cell': { fontSize: '0.75rem', py: 0.5 },
//                   }),
//                 }}
//                 initialState={{
//                   columns: {
//                     columnVisibilityModel: {
//                       userId: false,
//                       gamesList: false,
//                       ...(isMobile && { avgBetAmount: false, uniqueGamesPlayed: false, firstBetDate: false, lastBetDate: false }),
//                     },
//                   },
//                 }}
//                 getRowId={(row) => row.id}
//                 density={isMobile ? 'compact' : 'standard'}
//               />
//             </Paper>

//             {/* Data freshness indicator */}
//             <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: { xs: 'column', md: 'row' }, gap: 1 }}>
//               <Typography variant="caption" color="text.secondary">
//                 Data grouped by: {summary.groupedBy || 'phone'}
//                 {searchMode && <Chip label="Filtered" size="small" sx={{ ml: 1 }} />}
//               </Typography>
//               <Typography variant="caption" color="text.secondary">
//                 Showing {rows.length} customer{rows.length !== 1 ? 's' : ''} 
//                 {searchMode ? ` (Search: ${searchQuery})` : ' (All-Time Data)'}
//               </Typography>
//             </Box>
//           </>
//         )}

//         {/* No customers found */}
//         {!isLoading && customers.length === 0 && !searchMode && (
//           <Alert severity="warning" sx={{ mb: 3 }}>
//             No customer data available. This could be due to no betting activity or database connectivity issues.
//           </Alert>
//         )}
//       </Box>
//     </Container>
//   );
// };

// export default Customers;

// import React from 'react';
// import { useSelector } from 'react-redux';
// import { 
//   Box, 
//   Typography, 
//   Paper, 
//   Grid, 
//   Card, 
//   CardContent,
//   Alert,
//   Chip,
//   CircularProgress,
//   useTheme,
//   useMediaQuery,
//   Skeleton,
//   Stack,
//   IconButton,
//   Tooltip,
//   Container,
//   TextField,
//   InputAdornment,
//   Button,
//   Fade,
//   Collapse
// } from '@mui/material';
// import { DataGrid } from '@mui/x-data-grid';
// import { 
//   Refresh as RefreshIcon,
//   TrendingUp as TrendingUpIcon,
//   TrendingDown as TrendingDownIcon,
//   People as PeopleIcon,
//   AccountBalance as AccountBalanceIcon,
//   EmojiEvents as EmojiEventsIcon,
//   Analytics as AnalyticsIcon,
//   Search as SearchIcon,
//   Clear as ClearIcon,
//   Phone as PhoneIcon
// } from '@mui/icons-material';
// import { selectCurrentFiltersAsApiParams } from '../../store/slices/dashboardSlice';
// import { useGetCustomersQuery, useLazySearchCustomerByPhoneQuery } from '../../store/api/dashboardApi';

// const Customers = () => {
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down('md'));
//   const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
//   // Search state
//   const [searchQuery, setSearchQuery] = React.useState('');
//   const [searchMode, setSearchMode] = React.useState(false);
//   const [searchResults, setSearchResults] = React.useState(null);
  
//   const allTimeFilters = {};
  
//   const [paginationModel, setPaginationModel] = React.useState({
//     page: 0,
//     pageSize: isMobile ? 10 : 25,
//   });

//   // Main customers query (for all customers)
//   const { 
//     data: customersData, 
//     isLoading, 
//     error,
//     refetch,
//     isFetching 
//   } = useGetCustomersQuery(searchMode ? { phone: searchQuery, limit: 100 } : allTimeFilters);

//   // Lazy query for phone search
//   const [
//     searchByPhone, 
//     { 
//       data: phoneSearchData, 
//       isLoading: isSearching, 
//       error: searchError 
//     }
//   ] = useLazySearchCustomerByPhoneQuery();

//   React.useEffect(() => {
//     setPaginationModel(prev => ({
//       ...prev,
//       pageSize: isMobile ? 10 : 25
//     }));
//   }, [isMobile]);

//   // Handle search
//   const handleSearch = React.useCallback((query) => {
//     const trimmedQuery = query.trim();
    
//     if (!trimmedQuery) {
//       setSearchMode(false);
//       setSearchResults(null);
//       setSearchQuery('');
//       return;
//     }

//     setSearchQuery(trimmedQuery);
//     setSearchMode(true);
    
//     // Use the lazy query for dedicated phone search
//     searchByPhone(trimmedQuery);
//   }, [searchByPhone]);

//   // Handle search input change with debounce
//   const [searchInput, setSearchInput] = React.useState('');
//   const searchTimeoutRef = React.useRef();

//   const handleSearchInputChange = (event) => {
//     const value = event.target.value;
//     setSearchInput(value);
    
//     // Clear existing timeout
//     if (searchTimeoutRef.current) {
//       clearTimeout(searchTimeoutRef.current);
//     }
    
//     // Set new timeout for debounced search
//     searchTimeoutRef.current = setTimeout(() => {
//       if (value.trim()) {
//         handleSearch(value);
//       } else {
//         clearSearch();
//       }
//     }, 500); // 500ms debounce
//   };

//   const clearSearch = () => {
//     setSearchInput('');
//     setSearchQuery('');
//     setSearchMode(false);
//     setSearchResults(null);
//     if (searchTimeoutRef.current) {
//       clearTimeout(searchTimeoutRef.current);
//     }
//   };

//   // Handle search results
//   React.useEffect(() => {
//     if (phoneSearchData) {
//       setSearchResults(phoneSearchData);
//     }
//   }, [phoneSearchData]);

//   // Debug logging
//   React.useEffect(() => {
//     console.log('Customers Query State:', {
//       isLoading,
//       error,
//       customersData,
//       searchMode,
//       searchQuery,
//       searchResults,
//       filters: searchMode ? { phone: searchQuery } : allTimeFilters
//     });
    
//     if (customersData) {
//       console.log('Summary data:', customersData.summary);
//       console.log('Customers array length:', customersData.customers?.length);
//     }
//   }, [isLoading, error, customersData, searchMode, searchQuery, searchResults, allTimeFilters]);

//   // Get data based on search mode
//   const getDisplayData = () => {
//     if (searchMode && searchResults) {
//       if (searchResults.found) {
//         return {
//           summary: {
//             totalCustomers: 1,
//             totalPlayAmount: searchResults.customer.totalPlayAmount,
//             totalWinnings: searchResults.customer.totalWinnings,
//             avgCustomerValue: searchResults.customer.totalPlayAmount,
//             groupedBy: 'phone'
//           },
//           customers: [searchResults.customer]
//         };
//       } else {
//         return {
//           summary: {
//             totalCustomers: 0,
//             totalPlayAmount: 0,
//             totalWinnings: 0,
//             avgCustomerValue: 0,
//             groupedBy: 'phone'
//           },
//           customers: []
//         };
//       }
//     }
//     return customersData;
//   };

//   const displayData = getDisplayData();
//   const summary = displayData?.summary || {};
//   const customers = displayData?.customers || [];

//   // Data transformation for DataGrid
//   const rows = React.useMemo(() => {
//     console.log('Processing customers for DataGrid:', customers);
    
//     return customers.map((customer, index) => ({
//       id: customer.userId || customer.phone || `customer-${index}`,
//       phone: customer.phone,
//       userId: customer.userId,
//       totalBets: customer.totalBets,
//       totalPlayAmount: customer.totalPlayAmount,
//       totalWinnings: customer.totalWinnings,
//       netLoss: customer.netLoss,
//       avgBetAmount: customer.avgBetAmount,
//       firstBetDate: customer.firstBetDate ? new Date(customer.firstBetDate) : null,
//       lastBetDate: customer.lastBetDate ? new Date(customer.lastBetDate) : null,
//       uniqueGamesPlayed: customer.uniqueGamesPlayed,
//       gamesList: customer.gamesList,
//       winningBets: customer.winningBets,
//       winRatePercentage: customer.winRatePercentage,
//       filteredService: customer.filteredService,
//     }));
//   }, [customers]);

//   // Responsive column configuration
//   const getColumns = React.useCallback(() => {
//     const baseColumns = [
//       { 
//         field: 'phone', 
//         headerName: 'Phone', 
//         width: 140,
//         minWidth: 120,
//         flex: 1,
//         renderCell: (params) => (
//           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//             <PhoneIcon sx={{ fontSize: 16, color: 'primary.main' }} />
//             <Typography 
//               variant="body2" 
//               sx={{ 
//                 fontFamily: 'monospace',
//                 fontSize: { xs: '0.75rem', md: '0.875rem' },
//                 fontWeight: searchMode ? 'bold' : 'normal',
//                 color: searchMode ? 'primary.main' : 'inherit'
//               }}
//             >
//               {params.value}
//             </Typography>
//           </Box>
//         )
//       },
//       { 
//         field: 'totalBets', 
//         headerName: isMobile ? 'Bets' : 'Total Bets', 
//         width: isMobile ? 70 : 100, 
//         type: 'number',
//         minWidth: 60
//       },
//       { 
//         field: 'totalPlayAmount', 
//         headerName: isMobile ? 'Play' : 'Total Play', 
//         width: isMobile ? 90 : 120, 
//         type: 'number',
//         minWidth: 80,
//         valueFormatter: (value) => `$${value?.toFixed(2) || '0.00'}`
//       },
//       { 
//         field: 'totalWinnings', 
//         headerName: isMobile ? 'Win' : 'Total Winnings', 
//         width: isMobile ? 90 : 130, 
//         type: 'number',
//         minWidth: 80,
//         valueFormatter: (value) => `$${value?.toFixed(2) || '0.00'}`
//       },
//       { 
//         field: 'netLoss', 
//         headerName: isMobile ? 'Loss' : 'Net Loss', 
//         width: isMobile ? 80 : 100, 
//         type: 'number',
//         minWidth: 70,
//         valueFormatter: (value) => `$${value?.toFixed(2) || '0.00'}`,
//         renderCell: (params) => (
//           <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
//             {params.value > 0 ? (
//               <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main' }} />
//             ) : (
//               <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main' }} />
//             )}
//             <Typography 
//               variant="body2" 
//               color={params.value > 0 ? 'error.main' : 'success.main'}
//               sx={{ 
//                 fontWeight: 'medium',
//                 fontSize: { xs: '0.75rem', md: '0.875rem' }
//               }}
//             >
//               ${params.value?.toFixed(2) || '0.00'}
//             </Typography>
//           </Box>
//         )
//       },
//       { 
//         field: 'winRatePercentage', 
//         headerName: isMobile ? 'Rate' : 'Win Rate', 
//         width: isMobile ? 80 : 100, 
//         type: 'number',
//         minWidth: 70,
//         valueFormatter: (value) => `${value?.toFixed(1) || '0.0'}%`,
//         renderCell: (params) => (
//           <Chip 
//             label={`${params.value?.toFixed(1) || '0.0'}%`}
//             color={params.value >= 50 ? 'success' : 'warning'}
//             size="small"
//             variant="outlined"
//             sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
//           />
//         )
//       }
//     ];

//     if (!isMobile) {
//       baseColumns.push(
//         { field: 'avgBetAmount', headerName: 'Avg Bet', width: 100, type: 'number', valueFormatter: (value) => `$${value?.toFixed(2) || '0.00'}` },
//         { field: 'uniqueGamesPlayed', headerName: 'Games', width: 80, type: 'number' }
//       );
//     }

//     if (!isTablet) {
//       baseColumns.push(
//         { field: 'firstBetDate', headerName: 'First Bet', width: 120, type: 'dateTime', valueFormatter: (value) => value ? new Date(value).toLocaleDateString() : 'N/A' },
//         { field: 'lastBetDate', headerName: 'Last Bet', width: 120, type: 'dateTime', valueFormatter: (value) => value ? new Date(value).toLocaleDateString() : 'N/A' }
//       );
//     }

//     return baseColumns;
//   }, [isMobile, isTablet, searchMode]);

//   const summaryCards = [
//     { title: 'Total Customers', value: summary.totalCustomers?.toLocaleString() || '0', color: 'primary.main', icon: <PeopleIcon />, bgColor: 'primary.light' },
//     { title: 'Total Play Amount', value: `$${summary.totalPlayAmount?.toLocaleString() || '0.00'}`, color: 'info.main', icon: <AccountBalanceIcon />, bgColor: 'info.light' },
//     { title: 'Total Winnings', value: `$${summary.totalWinnings?.toLocaleString() || '0.00'}`, color: 'success.main', icon: <EmojiEventsIcon />, bgColor: 'success.light' },
//     { title: 'Avg Customer Value', value: `$${summary.avgCustomerValue?.toFixed(2) || '0.00'}`, color: 'warning.main', icon: <AnalyticsIcon />, bgColor: 'warning.light' }
//   ];

//   const LoadingSkeleton = () => (
//     <Box>
//       <Grid container spacing={3} sx={{ mb: 3 }}>
//         {[1, 2, 3, 4].map((index) => (
//           <Grid item xs={12} sm={6} md={3} key={index}>
//             <Card><CardContent><Skeleton variant="text" width="60%" height={32} /><Skeleton variant="text" width="40%" /></CardContent></Card>
//           </Grid>
//         ))}
//       </Grid>
//       <Paper sx={{ height: 400 }}><Skeleton variant="rectangular" height="100%" /></Paper>
//     </Box>
//   );

//   if (isLoading && !searchMode) {
//     return (
//       <Container maxWidth="xl"><Box sx={{ py: 2 }}><Skeleton variant="text" width="200px" height={40} sx={{ mb: 1 }} /><Skeleton variant="text" width="300px" height={24} sx={{ mb: 3 }} /><LoadingSkeleton /></Box></Container>
//     );
//   }

//   return (
//     <Container maxWidth="xl">
//       <Box sx={{ py: 2 }}>
//         {/* Header Section */}
//         <Box sx={{ 
//           display: 'flex', 
//           justifyContent: 'space-between', 
//           alignItems: { xs: 'flex-start', md: 'center' },
//           flexDirection: { xs: 'column', md: 'row' },
//           gap: 2,
//           mb: 3 
//         }}>
//           <Box>
//             <Typography 
//               variant={isMobile ? 'h5' : 'h4'}
//               component="h1" 
//               sx={{ fontWeight: 'bold', mb: 1 }}
//             >
//               Customers {searchMode && <Chip label="Search Results" color="primary" size="small" sx={{ ml: 1 }} />}
//             </Typography>
//             <Typography variant="body1" color="text.secondary">
//               {searchMode ? `Search results for: ${searchQuery}` : 'All-time customer accounts and betting activity analysis'}
//             </Typography>
//           </Box>
          
//           <Tooltip title="Refresh data">
//             <IconButton onClick={() => refetch()} disabled={isFetching || isSearching} color="primary" sx={{ bgcolor: 'primary.light', '&:hover': { bgcolor: 'primary.main', color: 'white' } }}>
//               <RefreshIcon />
//             </IconButton>
//           </Tooltip>
//         </Box>

//         {/* Search Section */}
//         <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
//           <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
//             <TextField
//               fullWidth
//               placeholder="Search by phone number (e.g., +1234567890, 123-456-7890)"
//               value={searchInput}
//               onChange={handleSearchInputChange}
//               InputProps={{
//                 startAdornment: (
//                   <InputAdornment position="start">
//                     <SearchIcon color="action" />
//                   </InputAdornment>
//                 ),
//                 endAdornment: searchInput && (
//                   <InputAdornment position="end">
//                     <IconButton onClick={clearSearch} size="small">
//                       <ClearIcon />
//                     </IconButton>
//                   </InputAdornment>
//                 )
//               }}
//               sx={{
//                 '& .MuiOutlinedInput-root': {
//                   bgcolor: 'white',
//                   '&:hover': { bgcolor: 'grey.50' }
//                 }
//               }}
//             />
            
//             {isSearching && (
//               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 'fit-content' }}>
//                 <CircularProgress size={20} />
//                 <Typography variant="body2">Searching...</Typography>
//               </Box>
//             )}
//           </Stack>

//           {/* Search Help */}
//           <Collapse in={!searchInput}>
//             <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
//               💡 Tip: Enter a phone number to find specific customer betting activity and statistics
//             </Typography>
//           </Collapse>
//         </Paper>

//         {/* Search Results Alert */}
//         {searchMode && searchResults && !searchResults.found && (
//           <Fade in>
//             <Alert severity="info" sx={{ mb: 3 }}>
//               <Typography variant="body2">
//                 No customer found with phone number: <strong>{searchQuery}</strong>
//               </Typography>
//               <Button onClick={clearSearch} size="small" sx={{ mt: 1 }}>
//                 View All Customers
//               </Button>
//             </Alert>
//           </Fade>
//         )}

//         {searchMode && searchResults?.found && (
//           <Fade in>
//             <Alert severity="success" sx={{ mb: 3 }}>
//               <Typography variant="body2">
//                 ✅ Found customer: <strong>{searchResults.customer.phone}</strong>
//               </Typography>
//               <Button onClick={clearSearch} size="small" sx={{ mt: 1 }}>
//                 View All Customers
//               </Button>
//             </Alert>
//           </Fade>
//         )}

//         {/* Error Handling */}
//         {(error || searchError) && (
//           <Alert severity="error" sx={{ mb: 3 }}>
//             Error loading customer data: {error?.message || searchError?.message || 'Unknown error'}
//           </Alert>
//         )}

//         {/* Loading indicator for refresh */}
//         {(isFetching || isSearching) && !isLoading && (
//           <Box display="flex" justifyContent="center" alignItems="center" sx={{ mb: 2 }}>
//             <CircularProgress size={20} />
//             <Typography variant="body2" sx={{ ml: 1 }}>
//               {isSearching ? 'Searching...' : 'Refreshing...'}
//             </Typography>
//           </Box>
//         )}

//         {/* Summary Cards */}
//         <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 3 }}>
//           {summaryCards.map((card, index) => (
//             <Grid item xs={6} sm={6} md={3} key={index}>
//               <Card sx={{ 
//                 height: '100%', 
//                 background: `linear-gradient(135deg, ${card.bgColor}20 0%, ${card.bgColor}10 100%)`, 
//                 border: `1px solid ${card.bgColor}40`, 
//                 transition: 'transform 0.2s, box-shadow 0.2s', 
//                 '&:hover': { transform: 'translateY(-2px)', boxShadow: theme.shadows[8] } 
//               }}>
//                 <CardContent sx={{ p: { xs: 2, md: 3 } }}>
//                   <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
//                     <Box sx={{ color: card.color, display: 'flex', fontSize: { xs: '1.2rem', md: '1.5rem' } }}>{card.icon}</Box>
//                     <Typography component="div" sx={{ color: card.color, fontWeight: 'bold', fontSize: { xs: '1rem', md: '1.25rem' } }}>{card.value}</Typography>
//                   </Stack>
//                   <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>{card.title}</Typography>
//                 </CardContent>
//               </Card>
//             </Grid>
//           ))}
//         </Grid>

//         {/* Data Grid Section */}
//         {!isLoading && customers.length > 0 && (
//           <>
//             <Paper sx={{ height: { xs: 500, md: 600 }, width: '100%', overflow: 'hidden' }}>
//               <DataGrid
//                 rows={rows}
//                 columns={getColumns()}
//                 paginationModel={paginationModel}
//                 onPaginationModelChange={setPaginationModel}
//                 pageSizeOptions={isMobile ? [10, 25] : [25, 50, 100]}
//                 rowCount={rows.length}
//                 loading={isLoading || isFetching || isSearching}
//                 disableRowSelectionOnClick
//                 sx={{
//                   '& .MuiDataGrid-root': { border: 'none' },
//                   '& .MuiDataGrid-cell': { borderBottom: '1px solid', borderBottomColor: 'divider', py: 1 },
//                   '& .MuiDataGrid-columnHeaders': { backgroundColor: 'grey.50', borderBottom: '2px solid', borderBottomColor: 'divider' },
//                   '& .MuiDataGrid-row:hover': { backgroundColor: 'action.hover' },
//                   '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: 'grey.25' },
//                   ...(isMobile && {
//                     '& .MuiDataGrid-columnHeader': { fontSize: '0.75rem', fontWeight: 'bold' },
//                     '& .MuiDataGrid-cell': { fontSize: '0.75rem', py: 0.5 },
//                   }),
//                 }}
//                 initialState={{
//                   columns: {
//                     columnVisibilityModel: {
//                       userId: false,
//                       gamesList: false,
//                       ...(isMobile && { avgBetAmount: false, uniqueGamesPlayed: false, firstBetDate: false, lastBetDate: false }),
//                     },
//                   },
//                 }}
//                 getRowId={(row) => row.id}
//                 density={isMobile ? 'compact' : 'standard'}
//               />
//             </Paper>

//             {/* Data freshness indicator */}
//             <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: { xs: 'column', md: 'row' }, gap: 1 }}>
//               <Typography variant="caption" color="text.secondary">
//                 Data grouped by: {summary.groupedBy || 'phone'}
//                 {searchMode && <Chip label="Filtered" size="small" sx={{ ml: 1 }} />}
//               </Typography>
//               <Typography variant="caption" color="text.secondary">
//                 Showing {rows.length} customer{rows.length !== 1 ? 's' : ''} 
//                 {searchMode ? ` (Search: ${searchQuery})` : ' (All-Time Data)'}
//               </Typography>
//             </Box>
//           </>
//         )}

//         {/* No customers found */}
//         {!isLoading && customers.length === 0 && !searchMode && (
//           <Alert severity="warning" sx={{ mb: 3 }}>
//             No customer data available. This could be due to no betting activity or database connectivity issues.
//           </Alert>
//         )}
//       </Box>
//     </Container>
//   );
// };

// export default Customers;

// import React from 'react';
// import { useSelector } from 'react-redux';
// import { 
//   Box, 
//   Typography, 
//   Paper, 
//   Grid, 
//   Card, 
//   CardContent,
//   Alert,
//   Chip,
//   CircularProgress,
//   useTheme,
//   useMediaQuery,
//   Skeleton,
//   Stack,
//   IconButton,
//   Tooltip,
//   Container
// } from '@mui/material';
// import { DataGrid } from '@mui/x-data-grid';
// import { 
//   Refresh as RefreshIcon,
//   TrendingUp as TrendingUpIcon,
//   TrendingDown as TrendingDownIcon,
//   People as PeopleIcon,
//   AccountBalance as AccountBalanceIcon,
//   EmojiEvents as EmojiEventsIcon,
//   Analytics as AnalyticsIcon
// } from '@mui/icons-material';
// import { selectCurrentFiltersAsApiParams } from '../../store/slices/dashboardSlice';
// import { useGetCustomersQuery } from '../../store/api/dashboardApi';

// const Customers = () => {
//   const theme = useTheme();
//   // These hooks are kept for logic that sx props can't handle,
//   // like changing component props (e.g., DataGrid density) or conditional column rendering.
//   const isMobile = useMediaQuery(theme.breakpoints.down('md'));
//   const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
//   const allTimeFilters = {};
  
//   const [paginationModel, setPaginationModel] = React.useState({
//     page: 0,
//     pageSize: isMobile ? 10 : 25,
//   });

//   const { 
//     data: customersData, 
//     isLoading, 
//     error,
//     refetch,
//     isFetching 
//   } = useGetCustomersQuery(allTimeFilters);

//   // This effect is correct for managing state based on screen size.
//   React.useEffect(() => {
//     setPaginationModel(prev => ({
//       ...prev,
//       pageSize: isMobile ? 10 : 25
//     }));
//   }, [isMobile]);

//   // Debug logging (original)
//   React.useEffect(() => {
//     console.log('Customers Query State:', {
//       isLoading,
//       error,
//       customersData,
//       filters: allTimeFilters
//     });
    
//     if (customersData) {
//       console.log('Summary data:', customersData.summary);
//       console.log('Customers array length:', customersData.customers?.length);
//       console.log('First customer (if any):', customersData.customers?.[0]);
//     }
//   }, [isLoading, error, customersData, allTimeFilters]);

//   const summary = customersData?.summary || {};
//   const customers = customersData?.customers || [];

//   // Data transformation for DataGrid (original)
//   const rows = React.useMemo(() => {
//     console.log('Processing customers for DataGrid:', customers);
    
//     return customers.map((customer, index) => ({
//       id: customer.userId || `customer-${index}`,
//       phone: customer.phone,
//       userId: customer.userId,
//       totalBets: customer.totalBets,
//       totalPlayAmount: customer.totalPlayAmount,
//       totalWinnings: customer.totalWinnings,
//       netLoss: customer.netLoss,
//       avgBetAmount: customer.avgBetAmount,
//       firstBetDate: customer.firstBetDate ? new Date(customer.firstBetDate) : null,
//       lastBetDate: customer.lastBetDate ? new Date(customer.lastBetDate) : null,
//       uniqueGamesPlayed: customer.uniqueGamesPlayed,
//       gamesList: customer.gamesList,
//       winningBets: customer.winningBets,
//       winRatePercentage: customer.winRatePercentage,
//       filteredService: customer.filteredService,
//     }));
//   }, [customers]);

//   // Responsive column configuration - this logic is complex and the original implementation is robust.
//   const getColumns = React.useCallback(() => {
//     const baseColumns = [
//       { 
//         field: 'phone', 
//         headerName: 'Phone', 
//         width: 140,
//         minWidth: 120,
//         flex: 1, // Use flex to allow the column to grow
//         renderCell: (params) => (
//           <Typography 
//             variant="body2" 
//             sx={{ 
//               fontFamily: 'monospace',
//               fontSize: { xs: '0.75rem', md: '0.875rem' } // Responsive font size
//             }}
//           >
//             {params.value}
//           </Typography>
//         )
//       },
//       { 
//         field: 'totalBets', 
//         headerName: isMobile ? 'Bets' : 'Total Bets', 
//         width: isMobile ? 70 : 100, 
//         type: 'number',
//         minWidth: 60
//       },
//       { 
//         field: 'totalPlayAmount', 
//         headerName: isMobile ? 'Play' : 'Total Play', 
//         width: isMobile ? 90 : 120, 
//         type: 'number',
//         minWidth: 80,
//         valueFormatter: (value) => `$${value?.toFixed(2) || '0.00'}`
//       },
//       { 
//         field: 'totalWinnings', 
//         headerName: isMobile ? 'Win' : 'Total Winnings', 
//         width: isMobile ? 90 : 130, 
//         type: 'number',
//         minWidth: 80,
//         valueFormatter: (value) => `$${value?.toFixed(2) || '0.00'}`
//       },
//       { 
//         field: 'netLoss', 
//         headerName: isMobile ? 'Loss' : 'Net Loss', 
//         width: isMobile ? 80 : 100, 
//         type: 'number',
//         minWidth: 70,
//         valueFormatter: (value) => `$${value?.toFixed(2) || '0.00'}`,
//         renderCell: (params) => (
//           <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
//             {params.value > 0 ? (
//               <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main' }} />
//             ) : (
//               <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main' }} />
//             )}
//             <Typography 
//               variant="body2" 
//               color={params.value > 0 ? 'error.main' : 'success.main'}
//               sx={{ 
//                 fontWeight: 'medium',
//                 fontSize: { xs: '0.75rem', md: '0.875rem' } // Responsive font size
//               }}
//             >
//               ${params.value?.toFixed(2) || '0.00'}
//             </Typography>
//           </Box>
//         )
//       },
//       { 
//         field: 'winRatePercentage', 
//         headerName: isMobile ? 'Rate' : 'Win Rate', 
//         width: isMobile ? 80 : 100, 
//         type: 'number',
//         minWidth: 70,
//         valueFormatter: (value) => `${value?.toFixed(1) || '0.0'}%`,
//         renderCell: (params) => (
//           <Chip 
//             label={`${params.value?.toFixed(1) || '0.0'}%`}
//             color={params.value >= 50 ? 'success' : 'warning'}
//             size="small"
//             variant="outlined"
//             sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }} // Responsive font size
//           />
//         )
//       }
//     ];

//     if (!isMobile) {
//       baseColumns.push(
//         { field: 'avgBetAmount', headerName: 'Avg Bet', width: 100, type: 'number', valueFormatter: (value) => `$${value?.toFixed(2) || '0.00'}` },
//         { field: 'uniqueGamesPlayed', headerName: 'Games', width: 80, type: 'number' }
//       );
//     }

//     if (!isTablet) {
//       baseColumns.push(
//         { field: 'firstBetDate', headerName: 'First Bet', width: 120, type: 'dateTime', valueFormatter: (value) => value ? new Date(value).toLocaleDateString() : 'N/A' },
//         { field: 'lastBetDate', headerName: 'Last Bet', width: 120, type: 'dateTime', valueFormatter: (value) => value ? new Date(value).toLocaleDateString() : 'N/A' }
//       );
//     }

//     return baseColumns;
//   }, [isMobile, isTablet]);

//   const summaryCards = [
//     { title: 'Total Customers', value: summary.totalCustomers?.toLocaleString() || '0', color: 'primary.main', icon: <PeopleIcon />, bgColor: 'primary.light' },
//     { title: 'Total Play Amount', value: `$${summary.totalPlayAmount?.toLocaleString() || '0.00'}`, color: 'info.main', icon: <AccountBalanceIcon />, bgColor: 'info.light' },
//     { title: 'Total Winnings', value: `$${summary.totalWinnings?.toLocaleString() || '0.00'}`, color: 'success.main', icon: <EmojiEventsIcon />, bgColor: 'success.light' },
//     { title: 'Avg Customer Value', value: `$${summary.avgCustomerValue?.toFixed(2) || '0.00'}`, color: 'warning.main', icon: <AnalyticsIcon />, bgColor: 'warning.light' }
//   ];

//   const LoadingSkeleton = () => (
//     <Box>
//       <Grid container spacing={3} sx={{ mb: 3 }}>
//         {[1, 2, 3, 4].map((index) => (
//           <Grid item xs={12} sm={6} md={3} key={index}>
//             <Card><CardContent><Skeleton variant="text" width="60%" height={32} /><Skeleton variant="text" width="40%" /></CardContent></Card>
//           </Grid>
//         ))}
//       </Grid>
//       <Paper sx={{ height: 400 }}><Skeleton variant="rectangular" height="100%" /></Paper>
//     </Box>
//   );

//   if (isLoading) {
//     return (
//       <Container maxWidth="xl"><Box sx={{ py: 2 }}><Skeleton variant="text" width="200px" height={40} sx={{ mb: 1 }} /><Skeleton variant="text" width="300px" height={24} sx={{ mb: 3 }} /><LoadingSkeleton /></Box></Container>
//     );
//   }

//   return (
//     <Container maxWidth="xl">
//       <Box sx={{ py: 2 }}>
//         {/* Header Section - Refactored with responsive sx props */}
//         <Box sx={{ 
//           display: 'flex', 
//           justifyContent: 'space-between', 
//           alignItems: { xs: 'flex-start', md: 'center' },
//           flexDirection: { xs: 'column', md: 'row' },
//           gap: 2,
//           mb: 3 
//         }}>
//           <Box>
//             <Typography 
//               variant={isMobile ? 'h5' : 'h4'} // variant prop does not take responsive objects
//               component="h1" 
//               sx={{ fontWeight: 'bold', mb: 1 }}
//             >
//               Customers
//             </Typography>
//             <Typography variant="body1" color="text.secondary">
//               All-time customer accounts and betting activity analysis
//             </Typography>
//           </Box>
          
//           <Tooltip title="Refresh data">
//             <IconButton onClick={() => refetch()} disabled={isFetching} color="primary" sx={{ bgcolor: 'primary.light', '&:hover': { bgcolor: 'primary.main', color: 'white' } }}>
//               <RefreshIcon />
//             </IconButton>
//           </Tooltip>
//         </Box>

//         {/* Debug/Error/Alert sections (original) */}
//         {process.env.NODE_ENV === 'development' && (
//           <Alert severity="info" sx={{ mb: 2 }}>
//              {/* ... content ... */}
//           </Alert>
//         )}
//         {error && (
//           <Alert severity="error" sx={{ mb: 3 }}>
//             {/* ... content ... */}
//           </Alert>
//         )}
//         {!isLoading && customers.length === 0 && (
//           <Alert severity="warning" sx={{ mb: 3 }}>
//             {/* ... content ... */}
//           </Alert>
//         )}
//         {isFetching && !isLoading && (
//           <Box display="flex" justifyContent="center" alignItems="center" sx={{ mb: 2 }}>
//             <CircularProgress size={20} />
//             <Typography variant="body2" sx={{ ml: 1 }}>Refreshing...</Typography>
//           </Box>
//         )}

//         {/* Summary Cards - Refactored with responsive spacing */}
//         <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 3 }}>
//           {summaryCards.map((card, index) => (
//             <Grid item xs={6} sm={6} md={3} key={index}>
//               <Card sx={{ height: '100%', background: `linear-gradient(135deg, ${card.bgColor}20 0%, ${card.bgColor}10 100%)`, border: `1px solid ${card.bgColor}40`, transition: 'transform 0.2s, box-shadow 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: theme.shadows[8] } }}>
//                 <CardContent sx={{ p: { xs: 2, md: 3 } }}> {/* Responsive padding */}
//                   <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
//                     <Box sx={{ color: card.color, display: 'flex', fontSize: { xs: '1.2rem', md: '1.5rem' } }}>{card.icon}</Box>
//                     <Typography component="div" sx={{ color: card.color, fontWeight: 'bold', fontSize: { xs: '1rem', md: '1.25rem' } }}>{card.value}</Typography>
//                   </Stack>
//                   <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>{card.title}</Typography>
//                 </CardContent>
//               </Card>
//             </Grid>
//           ))}
//         </Grid>

//         {/* Data Grid Section */}
//         {!isLoading && customers.length > 0 && (
//           <>
//             <Paper sx={{ height: { xs: 500, md: 600 }, width: '100%', overflow: 'hidden' }}>
//               <DataGrid
//                 rows={rows}
//                 columns={getColumns()}
//                 paginationModel={paginationModel}
//                 onPaginationModelChange={setPaginationModel}
//                 pageSizeOptions={isMobile ? [10, 25] : [25, 50, 100]}
//                 rowCount={rows.length}
//                 loading={isLoading || isFetching}
//                 disableRowSelectionOnClick
//                 sx={{
//                   '& .MuiDataGrid-root': { border: 'none' },
//                   '& .MuiDataGrid-cell': { borderBottom: '1px solid', borderBottomColor: 'divider', py: 1 },
//                   '& .MuiDataGrid-columnHeaders': { backgroundColor: 'grey.50', borderBottom: '2px solid', borderBottomColor: 'divider' },
//                   '& .MuiDataGrid-row:hover': { backgroundColor: 'action.hover' },
//                   '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: 'grey.25' },
//                   ...(isMobile && { // This block is a valid way to apply multiple conditional styles
//                     '& .MuiDataGrid-columnHeader': { fontSize: '0.75rem', fontWeight: 'bold' },
//                     '& .MuiDataGrid-cell': { fontSize: '0.75rem', py: 0.5 },
//                   }),
//                 }}
//                 initialState={{
//                   columns: {
//                     columnVisibilityModel: {
//                       userId: false,
//                       gamesList: false,
//                       ...(isMobile && { avgBetAmount: false, uniqueGamesPlayed: false, firstBetDate: false, lastBetDate: false }),
//                     },
//                   },
//                 }}
//                 getRowId={(row) => row.id}
//                 density={isMobile ? 'compact' : 'standard'}
//               />
//             </Paper>

//             {/* Data freshness indicator - Refactored with responsive sx props */}
//             <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: { xs: 'column', md: 'row' }, gap: 1 }}>
//               <Typography variant="caption" color="text.secondary">Data grouped by: {summary.groupedBy || 'phone'}</Typography>
//               <Typography variant="caption" color="text.secondary">Showing {rows.length} customers (All-Time Data)</Typography>
//             </Box>
//           </>
//         )}
//       </Box>
//     </Container>
//   );
// };

// export default Customers;



// ///     with dasshbard filters and RTK Query

// import React from 'react';
// import { useSelector } from 'react-redux';
// import { 
//   Box, 
//   Typography, 
//   Paper, 
//   Grid, 
//   Card, 
//   CardContent,
//   Alert,
//   Chip,
//   CircularProgress
// } from '@mui/material';
// import { DataGrid } from '@mui/x-data-grid';
// import { selectCurrentFiltersAsApiParams } from '../../store/slices/dashboardSlice';
// import { useGetCustomersQuery } from '../../store/api/dashboardApi';

// const Customers = () => {
//   const filters = useSelector(selectCurrentFiltersAsApiParams);
//   const [paginationModel, setPaginationModel] = React.useState({
//     page: 0,
//     pageSize: 25,
//   });

//   // Fetch customers data using RTK Query
//   const { 
//     data: customersData, 
//     isLoading, 
//     error,
//     refetch 
//   } = useGetCustomersQuery(filters);

//   // Debug logging
//   React.useEffect(() => {
//     console.log('Customers Query State:', {
//       isLoading,
//       error,
//       customersData,
//       filters
//     });
    
//     // Log the actual API response structure
//     if (customersData) {
//       console.log('Summary data:', customersData.summary);
//       console.log('Customers array length:', customersData.customers?.length);
//       console.log('First customer (if any):', customersData.customers?.[0]);
//     }
//   }, [isLoading, error, customersData, filters]);

//   const summary = customersData?.summary || {};
//   const customers = customersData?.customers || [];

//   // Transform customers data for DataGrid
//   const rows = React.useMemo(() => {
//     console.log('Processing customers for DataGrid:', customers);
    
//     return customers.map((customer, index) => ({
//       id: customer.userId || `customer-${index}`,
//       phone: customer.phone,
//       userId: customer.userId,
//       totalBets: customer.totalBets,
//       totalPlayAmount: customer.totalPlayAmount,
//       totalWinnings: customer.totalWinnings,
//       netLoss: customer.netLoss,
//       avgBetAmount: customer.avgBetAmount,
//       firstBetDate: customer.firstBetDate ? new Date(customer.firstBetDate) : null,
//       lastBetDate: customer.lastBetDate ? new Date(customer.lastBetDate) : null,
//       uniqueGamesPlayed: customer.uniqueGamesPlayed,
//       gamesList: customer.gamesList,
//       winningBets: customer.winningBets,
//       winRatePercentage: customer.winRatePercentage,
//       filteredService: customer.filteredService,
//     }));
//   }, [customers]);

//   const columns = [
//     { 
//       field: 'phone', 
//       headerName: 'Phone', 
//       width: 140,
//       renderCell: (params) => (
//         <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
//           {params.value}
//         </Typography>
//       )
//     },
//     { 
//       field: 'totalBets', 
//       headerName: 'Total Bets', 
//       width: 100, 
//       type: 'number'
//     },
//     { 
//       field: 'totalPlayAmount', 
//       headerName: 'Total Play', 
//       width: 120, 
//       type: 'number',
//       valueFormatter: (value) => `$${value?.toFixed(2) || '0.00'}`
//     },
//     { 
//       field: 'totalWinnings', 
//       headerName: 'Total Winnings', 
//       width: 130, 
//       type: 'number',
//       valueFormatter: (value) => `$${value?.toFixed(2) || '0.00'}`
//     },
//     { 
//       field: 'netLoss', 
//       headerName: 'Net Loss', 
//       width: 100, 
//       type: 'number',
//       valueFormatter: (value) => `$${value?.toFixed(2) || '0.00'}`,
//       renderCell: (params) => (
//         <Typography 
//           variant="body2" 
//           color={params.value > 0 ? 'error.main' : 'success.main'}
//           sx={{ fontWeight: 'medium' }}
//         >
//           ${params.value?.toFixed(2) || '0.00'}
//         </Typography>
//       )
//     },
//     { 
//       field: 'winRatePercentage', 
//       headerName: 'Win Rate', 
//       width: 100, 
//       type: 'number',
//       valueFormatter: (value) => `${value?.toFixed(1) || '0.0'}%`,
//       renderCell: (params) => (
//         <Chip 
//           label={`${params.value?.toFixed(1) || '0.0'}%`}
//           color={params.value >= 50 ? 'success' : 'warning'}
//           size="small"
//           variant="outlined"
//         />
//       )
//     },
//     { 
//       field: 'avgBetAmount', 
//       headerName: 'Avg Bet', 
//       width: 100, 
//       type: 'number',
//       valueFormatter: (value) => `$${value?.toFixed(2) || '0.00'}`
//     },
//     { 
//       field: 'uniqueGamesPlayed', 
//       headerName: 'Games', 
//       width: 80, 
//       type: 'number'
//     },
//     { 
//       field: 'firstBetDate', 
//       headerName: 'First Bet', 
//       width: 120, 
//       type: 'dateTime',
//       valueFormatter: (value) => value ? new Date(value).toLocaleDateString() : 'N/A'
//     },
//     { 
//       field: 'lastBetDate', 
//       headerName: 'Last Bet', 
//       width: 120, 
//       type: 'dateTime',
//       valueFormatter: (value) => value ? new Date(value).toLocaleDateString() : 'N/A'
//     },
//     { 
//       field: 'gamesList', 
//       headerName: 'Games List', 
//       width: 180,
//       renderCell: (params) => (
//         <Typography 
//           variant="body2" 
//           sx={{ 
//             fontSize: '0.75rem',
//             overflow: 'hidden',
//             textOverflow: 'ellipsis',
//             whiteSpace: 'nowrap'
//           }}
//           title={params.value}
//         >
//           {params.value || 'N/A'}
//         </Typography>
//       )
//     },
//   ];

//   // Summary cards data
//   const summaryCards = [
//     {
//       title: 'Total Customers',
//       value: summary.totalCustomers?.toLocaleString() || '0',
//       color: 'primary.main'
//     },
//     {
//       title: 'Total Play Amount',
//       value: `$${summary.totalPlayAmount?.toFixed(2) || '0.00'}`,
//       color: 'info.main'
//     },
//     {
//       title: 'Total Winnings',
//       value: `$${summary.totalWinnings?.toFixed(2) || '0.00'}`,
//       color: 'success.main'
//     },
//     {
//       title: 'Avg Customer Value',
//       value: `$${summary.avgCustomerValue?.toFixed(2) || '0.00'}`,
//       color: 'warning.main'
//     }
//   ];

//   return (
//     <Box>
//       <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
//         Customers
//       </Typography>
//       <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
//         Customer accounts and betting activity analysis
//       </Typography>

//       {/* Debug Information */}
//       {process.env.NODE_ENV === 'development' && (
//         <Alert severity="info" sx={{ mb: 2 }}>
//           <Typography variant="body2">
//             <strong>Debug Info:</strong><br/>
//             Loading: {isLoading.toString()}<br/>
//             Error: {error ? 'Yes' : 'No'}<br/>
//             Customers Count: {customers.length}<br/>
//             Summary Total: {summary.totalCustomers || 0}<br/>
//             Current Filters: {JSON.stringify(filters)}<br/>
//             <em>Try changing date range or filter settings if no data shows</em>
//           </Typography>
//         </Alert>
//       )}

//       {error && (
//         <Alert severity="error" sx={{ mb: 3 }}>
//           Error loading customers: {error?.data?.message || error?.message || JSON.stringify(error)}
//         </Alert>
//       )}

//       {/* Show message when no data */}
//       {!isLoading && customers.length === 0 && (
//         <Alert severity="warning" sx={{ mb: 3 }}>
//           <Typography variant="body2">
//             <strong>No customers found with current filters.</strong><br/>
//             Current filter: {JSON.stringify(filters)}<br/>
//             Try:
//             <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
//               <li>Changing the date range to include more days</li>
//               <li>Selecting "All Time" or "Monthly" filter</li>
//               <li>Removing service code filters</li>
//               <li>Checking if there's actual customer data in your database</li>
//             </ul>
//           </Typography>
//         </Alert>
//       )}

//       {isLoading && (
//         <Box display="flex" justifyContent="center" alignItems="center" sx={{ mb: 3 }}>
//           <CircularProgress />
//           <Typography variant="body2" sx={{ ml: 2 }}>
//             Loading customers...
//           </Typography>
//         </Box>
//       )}

//       {/* Summary Cards */}
//       <Grid container spacing={3} sx={{ mb: 3 }}>
//         {summaryCards.map((card, index) => (
//           <Grid item xs={12} sm={6} md={3} key={index}>
//             <Card sx={{ height: '100%' }}>
//               <CardContent>
//                 <Typography variant="h6" component="div" sx={{ color: card.color, mb: 1 }}>
//                   {card.value}
//                 </Typography>
//                 <Typography variant="body2" color="text.secondary">
//                   {card.title}
//                 </Typography>
//               </CardContent>
//             </Card>
//           </Grid>
//         ))}
//       </Grid>

//       {/* Show data only when not loading */}
//       {!isLoading && (
//         <>
//           {/* Customers Data Grid */}
//           <Paper sx={{ height: 600, width: '100%' }}>
//             <DataGrid
//               rows={rows}
//               columns={columns}
//               paginationModel={paginationModel}
//               onPaginationModelChange={setPaginationModel}
//               pageSizeOptions={[25, 50, 100]}
//               rowCount={rows.length}
//               loading={isLoading}
//               disableRowSelectionOnClick
//               sx={{
//                 '& .MuiDataGrid-row:hover': {
//                   backgroundColor: 'action.hover',
//                 },
//                 '& .MuiDataGrid-cell': {
//                   borderBottom: '1px solid',
//                   borderBottomColor: 'divider',
//                 },
//               }}
//               initialState={{
//                 columns: {
//                   columnVisibilityModel: {
//                     userId: false, // Hide user ID by default
//                     gamesList: false, // Hide games list by default
//                   },
//                 },
//               }}
//               getRowId={(row) => row.id}
//             />
//           </Paper>

//           {/* Data freshness indicator */}
//           {customersData && (
//             <Box sx={{ mt: 2, textAlign: 'right' }}>
//               <Typography variant="caption" color="text.secondary">
//                 Data grouped by: {summary.groupedBy || 'phone'} • 
//                 Showing {rows.length} customers
//               </Typography>
//             </Box>
//           )}
//         </>
//       )}
//     </Box>
//   );
// };

// export default Customers;
// import React from 'react';
// import { Box, Typography, Paper } from '@mui/material';
// import { DataGrid } from '@mui/x-data-grid';

// const Customers = () => {
//   const [loading, setLoading] = React.useState(false);
//   const [rows, setRows] = React.useState([]);
//   const [rowCount, setRowCount] = React.useState(0);
//   const [paginationModel, setPaginationModel] = React.useState({
//     page: 0,
//     pageSize: 25,
//   });

//   const columns = [
//     { field: 'id', headerName: 'ID', width: 90 },
//     { field: 'phone', headerName: 'Phone', width: 150 },
//     { field: 'email', headerName: 'Email', width: 200 },
//     { field: 'totalPlay', headerName: 'Total Play', width: 120, type: 'number' },
//     { field: 'totalWin', headerName: 'Total Win', width: 120, type: 'number' },
//     { field: 'status', headerName: 'Status', width: 120 },
//     { field: 'joinedAt', headerName: 'Joined', width: 150, type: 'dateTime' },
//   ];

//   return (
//     <Box>
//       <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
//         Customers
//       </Typography>
//       <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
//         Manage customer accounts and activity
//       </Typography>

//       <Paper sx={{ height: 600, width: '100%' }}>
//         <DataGrid
//           rows={rows}
//           columns={columns}
//           paginationModel={paginationModel}
//           onPaginationModelChange={setPaginationModel}
//           pageSizeOptions={[25, 50, 100]}
//           rowCount={rowCount}
//           loading={loading}
//           paginationMode="server"
//           sortingMode="server"
//           filterMode="server"
//           disableRowSelectionOnClick
//         />
//       </Paper>
//     </Box>
//   );
// };

// export default Customers;